import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Fill, Stroke, Circle } from 'ol/style';
import { fromLonLat, toLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import GeoJSON from 'ol/format/GeoJSON';
import BaseLayer from 'ol/layer/Base';
import useResponsive from '../../hooks/useResponsive';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import FaunaSpeciesPopup from '../Map/FaunaSpeciesPopup';
import { FaunaSpecies } from '../../apiService/ObservationApiService';
import layerDataService, { LayerData } from '../../apiService/LayerDataService';
import 'ol/ol.css';
import { all as allStrategy } from 'ol/loadingstrategy';

// GeoServer configuration - adjust these URLs based on your proxy setup
const proxy = import.meta.env.VITE_APP_PROXY_URL;
const geoserverWorkspace = import.meta.env.VITE_APP_GEOSERVER_WORKSPACE;
/**
 * MapComponent - An interactive map component with OpenLayers
 * 
 * Features:
 * - OSM base layer
 * - WMS layers for observation data (obs_areas, obs_neighborhood, obs_species)
 * - WFS layer for projects (editable vector data from GeoServer)
 * - Optional project location marker
 * - Click handler for species selection
 * 
 * Layer order (bottom to top):
 * 1. OSM base map
 * 2. WMS observation layers (if enabled)
 * 3. WFS projects layer (if enabled)
 * 4. Project marker (if coordinates provided)
 */
interface MapComponentProps {
    projectData?: any;
    onSpeciesSelect?: (species: FaunaSpecies, location: { lat: number; lng: number }) => void;
    // New props for layer control
    showWMSLayers?: boolean;
    showWFSLayers?: boolean;
    projectId?: string | number;
    taskId?: string | number;
    layerId?: string | number | null;
    // Authentication props
    authUsername?: string;
    authPassword?: string;
    authToken?: string;
    customerId?: string;
    // Dynamic layer loading
    enableDynamicLayers?: boolean;
    // Center pointer mode
    showCenterPointer?: boolean;
    // Observation interaction props
    enableObservationInteraction?: boolean;
    observationLayerName?: string;
    onObservationClick?: (observationId: string, observationData: any, location: { lat: number; lng: number }) => void;
    // Edit mode props
    editMode?: boolean;
    selectedObservationId?: number | null;
    onObservationLocationUpdate?: (observationId: number, newLocation: { lat: number; lng: number }) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
    projectData,
    onSpeciesSelect,
    showWMSLayers = true,
    showWFSLayers = true,
    projectId = 1,
    taskId,
    layerId,
    authUsername,
    authPassword,
    authToken,
    customerId,
    enableDynamicLayers = true,
    showCenterPointer = true,
    enableObservationInteraction = true,
    observationLayerName = `${geoserverWorkspace}:obs_species`,
    onObservationClick,
    editMode = false,
    selectedObservationId = null,
    onObservationLocationUpdate
}) => {
    const { isDesktop, isMobile } = useResponsive();
    const { windowHeight } = useWindowDimensions();
    const theme = useTheme();
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<Map | null>(null);
    // Hybrid touch+mouse laptops can fire a touch-based click and then a
    // browser-generated compatibility mouse click (~300ms later) for a single tap.
    // Ignore a second click that lands this soon after the first.
    const lastClickHandledAtRef = useRef<number>(0);

    // State for the fauna species popup
    const [showSpeciesPopup, setShowSpeciesPopup] = useState(false);
    const [clickedLocation, setClickedLocation] = useState<{ lat: number; lng: number } | null>(null);

    // State for dynamic layers from LayerDataService
    const [dynamicLayersData, setDynamicLayersData] = useState<LayerData[]>([]);

    const [observationVectorLayer, setObservationVectorLayer] = useState<VectorLayer<VectorSource> | null>(null);

    // Refs to access current values in click handler without triggering re-renders
    const editModeRef = useRef(editMode);
    const selectedObservationIdRef = useRef(selectedObservationId);
    const onObservationLocationUpdateRef = useRef(onObservationLocationUpdate);
    const observationVectorLayerRef = useRef(observationVectorLayer);

    // Update refs when values change
    useEffect(() => {
        editModeRef.current = editMode;
    }, [editMode]);

    useEffect(() => {
        selectedObservationIdRef.current = selectedObservationId;
    }, [selectedObservationId]);

    useEffect(() => {
        onObservationLocationUpdateRef.current = onObservationLocationUpdate;
    }, [onObservationLocationUpdate]);

    useEffect(() => {
        observationVectorLayerRef.current = observationVectorLayer;
    }, [observationVectorLayer]);

    // Always use coordinates from projectData
    const stableCoordinates = useMemo(() => {
        if (projectData && projectData.latitude != null && projectData.longitude != null) {
            const lat = typeof projectData.latitude === 'string' ? parseFloat(projectData.latitude) : projectData.latitude;
            const lng = typeof projectData.longitude === 'string' ? parseFloat(projectData.longitude) : projectData.longitude;
            if (!isNaN(lat) && !isNaN(lng)) {
                return { lat, lng };
            }
        }
        // Default coordinates (Eindhoven area)
        return { lat: 51.4203, lng: 5.4003 };
    }, [projectData]);

    const mapHeight = isDesktop ? Math.min(windowHeight - 190, 600) : Math.min(windowHeight - 300, 500);

    // Add a ref to track if we just updated the location
    const justUpdatedLocationRef = useRef(false);

    // Effect to focus camera on selected observation in edit mode and update styles
    useEffect(() => {
        if (!observationVectorLayer) {
            return;
        }

        const source = observationVectorLayer.getSource();
        if (!source) return;

        const features = source.getFeatures();

        // Re-style all features based on selection state
        features.forEach((feature) => {
            const id = feature.get('obs_species_id') || feature.get('id') || feature.getId();
            const isSelected = editMode && selectedObservationId && id && id.toString() === selectedObservationId.toString();
        });

        // Focus camera on selected observation if in edit mode
        if (editMode && selectedObservationId && mapInstanceRef.current) {
            const selectedFeature = features.find(
                (feature) => {
                    const id = feature.get('obs_species_id') || feature.get('id') || feature.getId();
                    return id && id.toString() === selectedObservationId.toString();
                }
            );

            if (selectedFeature) {
                const geometry = selectedFeature.getGeometry();
                if (geometry instanceof Point) {
                    const coordinates = geometry.getCoordinates();
                    const view = mapInstanceRef.current.getView();

                    // Smoothly animate to the selected observation
                    view.animate({
                        center: coordinates,
                        duration: 500,
                        zoom: Math.max(view.getZoom() || 18, 18) // Zoom in if needed
                    });

                } else {
                    console.warn('Selected feature geometry is not a Point');
                }
            } else {
                console.warn('Selected feature not found in features array');
            }
        }

        // Force a refresh of the layer
        if (mapInstanceRef.current) {
            mapInstanceRef.current.render();
        }
    }, [editMode, selectedObservationId, observationVectorLayer]);

    // Load WFS observation layer only when entering edit mode; remove it when exiting.
    // This avoids loading all observation features into memory on every map mount.
    useEffect(() => {
        if (!mapInstanceRef.current || !enableObservationInteraction || !projectId || !taskId) return;

        if (editMode && !observationVectorLayerRef.current) {
            const layer = createObservationWFSLayer();
            mapInstanceRef.current.addLayer(layer);
            setObservationVectorLayer(layer);
        } else if (!editMode && observationVectorLayerRef.current) {
            const source = observationVectorLayerRef.current.getSource();
            if (source) source.clear();
            mapInstanceRef.current.removeLayer(observationVectorLayerRef.current);
            setObservationVectorLayer(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode]);

    // Reload map on 'reloadMap' event
    useEffect(() => {
        const handleReloadMap = async () => {
            if (mapInstanceRef.current) {
                const map = mapInstanceRef.current;

                // Remove all non-base tile layers (WMS layers)
                const layersToRemove: TileLayer<any>[] = [];
                map.getLayers().forEach((layer) => {
                    if (layer instanceof TileLayer && !(layer.getSource() instanceof OSM)) {
                        layersToRemove.push(layer);
                    }
                });
                layersToRemove.forEach((layer) => {
                    layer.getSource()?.dispose(); // free tile cache before removing
                    map.removeLayer(layer);
                });

                // Clear cache for remaining tile layers (including OSM)
                map.getLayers().forEach((layer) => {
                    if (layer instanceof TileLayer) {
                        const source = layer.getSource();
                        if (source) {
                            // Clear tile cache if available
                            if (typeof source.clear === 'function') {
                                source.clear();
                            }
                            // Refresh the layer
                            if (typeof source.refresh === 'function') {
                                source.refresh();
                            }
                        }
                    }
                });

                // Reload observation layer if enabled
                if (enableObservationInteraction && observationVectorLayer) {
                    const source = observationVectorLayer.getSource();
                    if (source) {
                        source.clear();
                        source.refresh();
                    }
                }

                // Trigger a reload of dynamic layers by calling loadDynamicLayers
                await loadDynamicLayers();
            }
        };
        window.addEventListener('reloadMap', handleReloadMap);
        return () => {
            window.removeEventListener('reloadMap', handleReloadMap);
        };
    }, [enableDynamicLayers, projectId, taskId, layerId, enableObservationInteraction, observationVectorLayer]);

    // Load dynamic layers when parameters change
    useEffect(() => {
        loadDynamicLayers();
    }, [enableDynamicLayers, projectId, taskId, layerId]);

    useEffect(() => {
        if (!mapRef.current) return;

        // Clean up previous map instance and layers
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setTarget(undefined);
            mapInstanceRef.current.dispose();
            mapInstanceRef.current = null;
        }

        // Create base OSM layer
        const osmLayer = new TileLayer({
            source: new OSM({ cacheSize: 64 }),
        });

        // Create layers array with proper typing
        const layers: BaseLayer[] = [osmLayer];

        // Create vector source for local project marker only
        const localVectorSource = new VectorSource();

        // Add project location marker only if valid coordinates from projectData
        if (stableCoordinates) {
            const markerFeature = new Feature({
                geometry: new Point(fromLonLat([stableCoordinates.lng, stableCoordinates.lat])),
                name: projectData?.project_name || 'Project Location',
                address: projectData?.project_address || '',
            });

            markerFeature.setStyle(new Style({
                image: new Circle({
                    radius: 8,
                    fill: new Fill({ color: '#3388ff' }),
                    stroke: new Stroke({ color: '#ffffff', width: 2 }),
                }),
            }));

            localVectorSource.addFeature(markerFeature);
        }

        // Only add local vector layer if it has features
        if (localVectorSource.getFeatures().length > 0) {
            const localVectorLayer = new VectorLayer({
                source: localVectorSource,
            });
            layers.push(localVectorLayer);
        }

        // Add dynamic layers from LayerDataService if available
        if (enableDynamicLayers && dynamicLayersData.length > 0) {
            dynamicLayersData.forEach((layerData, index: number) => {
                // Get SLD style name automatically from LayerDataService
                const sldStyle = layerDataService.getSLDStyleName(layerData);
                const dynamicLayer = createDynamicWMSLayer(layerData, sldStyle);
                dynamicLayer.setZIndex(2000 + index);
                dynamicLayer.setOpacity(1);
                dynamicLayer.setVisible(true);
                layers.push(dynamicLayer);
            });
        }

        // WFS observation layer is loaded lazily when editMode becomes true (see useEffect below)
        // to avoid loading all observation features into memory on every map mount

        if (stableCoordinates) {
            const map = new Map({
                target: mapRef.current,
                layers: layers,
                view: new View({
                    center: fromLonLat([stableCoordinates.lng, stableCoordinates.lat]),
                    zoom: 18,
                }),
                controls: defaultControls(),
                // On mobile (2x–3x pixel ratio) the canvas is 4–9× larger in memory.
                // Rendering at 1× keeps the canvas footprint manageable on iOS Safari.
                pixelRatio: isMobile ? 1 : window.devicePixelRatio,
                // OL's default (1px) is tighter than the pointer jitter touchscreen-laptop
                // digitizers report on a stationary tap, causing it to be misread as a drag
                // and suppressing the click that opens the species popup.
                moveTolerance: 8,
            });

            map.on('click', (event: any) => {
                // Hybrid touch+mouse devices can dispatch two click events for one tap
                // (a touch-based one, then a delayed compatibility mouse click). Ignore
                // the second one so it can't reopen/re-trigger the species popup.
                const now = Date.now();
                if (now - lastClickHandledAtRef.current < 500) {
                    return;
                }
                lastClickHandledAtRef.current = now;

                // Check if we clicked on an observation first
                const pixel = map.getEventPixel(event.originalEvent);
                let clickedObservation = false;

                if (enableObservationInteraction && observationVectorLayerRef.current) {
                    map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                        if (layer === observationVectorLayerRef.current) {
                            clickedObservation = true;
                            // Handle observation click
                            const observationId = feature.get('obs_species_id') || feature.get('id') || feature.getId();
                            const geometry = feature.getGeometry();

                            if (geometry instanceof Point && onObservationClick) {
                                const coordinates = toLonLat(geometry.getCoordinates());
                                const observationData = feature.getProperties();
                                onObservationClick(
                                    observationId?.toString() || '',
                                    observationData,
                                    { lat: coordinates[1], lng: coordinates[0] }
                                );
                            }

                            return true;
                        }
                        return false;
                    });
                }

                // Helper function to check if click is within camera focus center
                const isClickWithinCenterFocus = (clickPixel: number[]) => {
                    if (!showCenterPointer) return true; // If no center pointer, allow clicks anywhere

                    const mapSize = map.getSize();
                    if (!mapSize) return false;

                    const mapCenterX = mapSize[0] / 2;
                    const mapCenterY = mapSize[1] / 2;

                    // Camera focus area is 60px x 60px centered on map
                    const focusSize = 60;
                    const focusHalfSize = focusSize / 2;

                    const isWithinX = clickPixel[0] >= (mapCenterX - focusHalfSize) &&
                        clickPixel[0] <= (mapCenterX + focusHalfSize);
                    const isWithinY = clickPixel[1] >= (mapCenterY - focusHalfSize) &&
                        clickPixel[1] <= (mapCenterY + focusHalfSize);

                    return isWithinX && isWithinY;
                };

                // Handle edit mode: update selected observation location on center click
                // Use refs to get current values
                const currentEditMode = editModeRef.current;
                const currentSelectedObservationId = selectedObservationIdRef.current;
                const currentOnObservationLocationUpdate = onObservationLocationUpdateRef.current;
                const currentObservationVectorLayer = observationVectorLayerRef.current;

                if (currentEditMode && currentSelectedObservationId && isClickWithinCenterFocus(pixel) && showCenterPointer) {
                    const center = map.getView().getCenter();
                    if (center && currentOnObservationLocationUpdate && currentObservationVectorLayer) {
                        const newCoordinate = toLonLat(center);
                        const newLocation = { lat: newCoordinate[1], lng: newCoordinate[0] };
                        // Update the feature position on the map
                        const source = currentObservationVectorLayer.getSource();
                        if (source) {
                            const features = source.getFeatures();
                            const selectedFeature = features.find(
                                (feature) => {
                                    const id = feature.get('obs_species_id') || feature.get('id') || feature.getId();
                                    return id && id.toString() === currentSelectedObservationId.toString();
                                }
                            );

                            if (selectedFeature && selectedFeature.getGeometry() instanceof Point) {
                                // Update the feature's geometry to the new location
                                (selectedFeature.getGeometry() as Point).setCoordinates(center);

                                // Force source to notify listeners of the change
                                source.changed();

                                // Set flag to prevent the effect from overriding this animation
                                justUpdatedLocationRef.current = true;

                                // Trigger callback to update in parent component/API
                                currentOnObservationLocationUpdate(currentSelectedObservationId, newLocation);

                                // Animate camera to the updated location
                                const view = map.getView();
                                view.animate({
                                    center: center,
                                    duration: 500,
                                    zoom: Math.max(view.getZoom() || 18, 18)
                                });
                            } else {
                                console.warn('Could not find selected feature for location update');
                            }
                        }
                    }
                }
                // Only show species popup if no observation was clicked and click is within center focus 
                else if (!clickedObservation && isClickWithinCenterFocus(pixel) && (!currentEditMode || !currentSelectedObservationId)) {
                    if (showCenterPointer) {
                        // Use center of map view instead of click location
                        const center = map.getView().getCenter();
                        if (center) {
                            const coordinate = toLonLat(center);
                            setClickedLocation({ lat: coordinate[1], lng: coordinate[0] });
                            setShowSpeciesPopup(true);
                        }
                    } else {
                        // Original behavior - use click location
                        const coordinate = toLonLat(event.coordinate);
                        setClickedLocation({ lat: coordinate[1], lng: coordinate[0] });
                        setShowSpeciesPopup(true);
                    }
                }
            });

            mapInstanceRef.current = map;

            return () => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.setTarget(undefined);
                    mapInstanceRef.current.dispose(); // frees tile caches, canvas, and event listeners
                    mapInstanceRef.current = null;
                }
            };
        }
        // Note: mapHeight is intentionally excluded from deps.
        // Height changes are handled by a separate useEffect that calls map.updateSize()
        // instead of destroying and recreating the entire map (which causes tile reload flicker on mobile scroll).
        // dynamicLayersData intentionally excluded — added to the live map in a separate effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableCoordinates, projectData, showWMSLayers, showWFSLayers, projectId, layerId, authUsername, authPassword, customerId, enableDynamicLayers, showCenterPointer, enableObservationInteraction, observationLayerName]);

    // Add dynamic WMS layers to the live map instance without recreating the map.
    // On mobile, cap at 3 layers to limit tile cache memory — large-observation tasks
    // can return many layer types which each hold their own tile images.
    useEffect(() => {
        if (!mapInstanceRef.current || dynamicLayersData.length === 0) return;

        const map = mapInstanceRef.current;

        // Remove previously added dynamic layers and dispose their sources
        const toRemove: TileLayer<any>[] = [];
        map.getLayers().forEach((layer) => {
            if (layer instanceof TileLayer && !(layer.getSource() instanceof OSM)) {
                toRemove.push(layer as TileLayer<any>);
            }
        });
        toRemove.forEach((layer) => {
            layer.getSource()?.dispose();
            map.removeLayer(layer);
        });

        // On mobile, limit layers to reduce peak memory
        const layersToAdd = isMobile ? dynamicLayersData.slice(0, 3) : dynamicLayersData;
        layersToAdd.forEach((layerData, index) => {
            const sldStyle = layerDataService.getSLDStyleName(layerData);
            const dynamicLayer = createDynamicWMSLayer(layerData, sldStyle);
            dynamicLayer.setZIndex(2000 + index);
            dynamicLayer.setOpacity(1);
            dynamicLayer.setVisible(true);
            map.addLayer(dynamicLayer);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicLayersData, isMobile]);

    // Separate effect: when mapHeight changes (e.g. mobile URL bar show/hide),
    // just tell the existing OL map to recalculate its viewport size —
    // no need to destroy and recreate the map.
    useEffect(() => {
        if (mapInstanceRef.current) {
            // Allow the DOM to update with the new container height first
            requestAnimationFrame(() => {
                mapInstanceRef.current?.updateSize();
            });
        }
    }, [mapHeight]);

    // Function to create dynamic WMS layer using LayerDataService
    const createDynamicWMSLayer = (layerData: LayerData, styles?: string) => {
        // Use the enhanced layer configuration method
        const layerConfig = layerDataService.createLayerConfig(layerData, proxy, styles);
        // Add cache-busting param to force fresh tiles
        const wmsParams = {
            ...layerConfig.params,
            _t: Date.now(),
        };
        return new TileLayer({
            source: new TileWMS({
                url: layerConfig.url,
                params: wmsParams,
                serverType: 'geoserver',
                transition: 0,
                cacheSize: 64,
                tileLoadFunction: function (tile: any, src: string) {
                    const img = tile.getImage();
                    const xhr = new XMLHttpRequest();
                    xhr.responseType = 'blob';

                    xhr.addEventListener('load', function () {
                        if (xhr.status === 200) {
                            const objectUrl = URL.createObjectURL(xhr.response);
                            img.src = objectUrl;
                            // Revoke after image loads to free the blob from memory
                            img.onload = () => URL.revokeObjectURL(objectUrl);
                        } else {
                            console.error(`Dynamic WMS tile load failed: ${xhr.status} ${xhr.statusText}`);
                            console.error('Response headers:', xhr.getAllResponseHeaders());
                            // Don't try to read responseText when responseType is 'blob'
                            console.error(`Response size:`, xhr.response?.size || 'unknown');
                        }
                    });

                    xhr.addEventListener('error', function () {
                        console.error(`Dynamic WMS tile load error for: ${src}`);
                    });

                    xhr.open('GET', src);

                    // Add authentication headers for dynamic layers
                    if (authToken && authUsername && customerId) {
                        const userName = authUsername.replace(/^["']|["']$/g, '');
                        const token = authToken.replace(/^["']|["']$/g, '');

                        xhr.setRequestHeader('X-Proxy-Token', token);
                        xhr.setRequestHeader('X-Proxy-Username', userName);
                        xhr.setRequestHeader('X-Proxy-Customer-Id', customerId);
                    } else {
                        console.warn(`Missing authentication credentials for dynamic WMS tiles ${layerData.typeName}`);
                    }

                    xhr.send();
                }
            }),
            opacity: 1,
            visible: true
        });
    };

    // Function to load dynamic layers from LayerDataService
    const loadDynamicLayers = async () => {
        if (!enableDynamicLayers) {
            return;
        }

        if (!projectId || !taskId) {
            console.warn('Missing required parameters for dynamic layers:', {
                projectId: !!projectId,
                taskId: !!taskId
            });
            return;
        }

        try {
            let layerIdParam: number | null = null;
            if (layerId !== undefined && layerId !== null && layerId !== '') {
                layerIdParam = Number(layerId);
            } else {
                layerIdParam = null;
            }
            const layerData = await layerDataService.getLayerData(Number(projectId), Number(taskId), layerIdParam);
            if (layerData.length === 0) {
                console.warn('No dynamic layers returned');
            } else {
                setDynamicLayersData(layerData);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load dynamic layers';
            console.error('Error loading dynamic layers:', errorMessage);
            setDynamicLayersData([]);
        }
    };

    // Function to create authentication headers
    const createAuthHeaders = () => {
        const headers: Record<string, string> = {};
        if (authToken && authUsername && customerId) {
            const userName = authUsername.replace(/^["']|["']$/g, '');
            const token = authToken.replace(/^["']|["']$/g, '');

            headers['X-Proxy-Token'] = token;
            headers['X-Proxy-Username'] = userName;
            headers['X-Proxy-Customer-Id'] = customerId;
        }
        return headers;
    };

    // Function to create WFS vector source with proper URL structure
    const createWFSVectorSource = (typename: string) => {
        const wfsUrl = `${proxy}/${geoserverWorkspace}/wfs/project/${projectId}/task/${taskId}`;

        const vectorSource = new VectorSource<Feature>({
            format: new GeoJSON<Feature>(),
            strategy: allStrategy, // Add loading strategy
            loader: function (extent, resolution, projection, success, failure) {
                // Validate required parameters before making request
                if (!projectId || !taskId) {
                    console.error('Missing required parameters for WFS request');
                    if (failure) failure();
                    return;
                }

                const baseUrl = wfsUrl +
                    '?service=WFS&' +
                    'version=1.1.0&request=GetFeature&typeName=' + typename + '&' +
                    'outputFormat=application/json&srsname=EPSG:3857' +
                    '&maxFeatures=200';

                const xhr = new XMLHttpRequest();
                xhr.open('GET', baseUrl);

                // Add auth headers if available
                if (authUsername) {
                    const headers = createAuthHeaders();
                    Object.keys(headers).forEach(key => {
                        xhr.setRequestHeader(key, headers[key]);
                    });
                }

                xhr.onload = function () {
                    if (xhr.status === 200) {
                        try {
                            // Check if response is XML error instead of JSON
                            if (xhr.responseText.trim().startsWith('<?xml')) {
                                console.error('WFS returned XML error:', xhr.responseText);
                                if (failure) failure();
                                return;
                            }

                            const features = new GeoJSON().readFeatures(xhr.responseText, {
                                dataProjection: 'EPSG:3857',
                                featureProjection: projection
                            });

                            // Explicitly add features to the source
                            if (features.length > 0) {
                                vectorSource.clear();
                                vectorSource.addFeatures(features);
                            }

                            if (success) success(features);
                        } catch (e) {
                            console.error('Error parsing WFS response:', e);
                            if (failure) failure();
                        }
                    } else {
                        console.error('WFS request failed:', xhr.status, xhr.statusText);
                        if (failure) failure();
                    }
                };

                xhr.onerror = function () {
                    console.error('WFS request error for URL:', baseUrl);
                    if (failure) failure();
                };

                xhr.send();
            }
        });

        return vectorSource;
    };


    // Function to create draggable observation WFS layer
    const createObservationWFSLayer = () => {
        // Use the reusable createWFSVectorSource function
        const vectorSource = createWFSVectorSource(observationLayerName);

        // Add event listener to style features when they are loaded
        vectorSource.on('featuresloadend', () => {
            const features = vectorSource.getFeatures();

            // Style all observations with custom icons
            features.forEach((feature, index) => {
                const geometry = feature.getGeometry();
                const coordinates = geometry instanceof Point ? geometry.getCoordinates() : null;
                const properties = feature.getProperties();
                const id = feature.get('obs_species_id') || feature.get('id') || feature.getId();
                const isSelected = editMode && selectedObservationId && id && id.toString() === selectedObservationId.toString();
            });

            // Force a refresh of the layer
            if (mapInstanceRef.current) {
                mapInstanceRef.current.render();
            }
        });

        // Add event listener for when features are added to the source
        vectorSource.on('addfeature', (event) => {
            const feature = event.feature;
        });

        // Also listen for errors
        vectorSource.on('featuresloaderror', (event) => {
            console.error('Features load error:', event);
        });

        return new VectorLayer({
            source: vectorSource,
            opacity: 0,
            zIndex: 1000,
            visible: true
        });
    };

    // Handle species selection
    const handleSpeciesSelect = (species: FaunaSpecies) => {
        if (clickedLocation && onSpeciesSelect) {
            onSpeciesSelect(species, clickedLocation);
        }
        setShowSpeciesPopup(false);
        setClickedLocation(null);
    };

    // Handle popup close
    const handlePopupClose = () => {
        setShowSpeciesPopup(false);
        setClickedLocation(null);
    };

    return (
        <>
            <Box id="map"
                sx={{
                    display: "flex",
                    width: isDesktop ? '100%' : "90%",
                    position: 'relative',
                    overflow: 'hidden',
                    border: 1,
                    borderColor: '#f2f0ef',
                    borderRadius: 2,
                    height: mapHeight,
                    minHeight: "400px",
                }}
            >
                <div
                    ref={mapRef}
                    onContextMenu={(e) => e.preventDefault()}
                    // Suppresses the browser's delayed ~300ms "ghost" compatibility mouse
                    // events (mousedown/mouseup/click) that hybrid touch+mouse devices fire
                    // after a real touch tap. Without this, that delayed click lands on
                    // whatever now renders at the same screen position (e.g. a tile in the
                    // freshly-opened species popup) and can auto-trigger it. OL's own click
                    // handling uses Pointer Events (pointerdown/pointerup), which this does
                    // not affect, so normal tap-to-open behavior is unchanged.
                    onTouchEnd={(e) => e.preventDefault()}
                    style={{
                        height: '100%',
                        width: '100%',
                        borderRadius: '8px',
                        // Belt-and-suspenders alongside onContextMenu: some Chromium/Windows
                        // touch digitizers show the touch-and-hold callout/selection UI before
                        // (or independent of) the cancelable 'contextmenu' DOM event.
                        WebkitTouchCallout: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                    }}
                />

                {/* Camera Focus Center Pointer */}
                {showCenterPointer && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            zIndex: 1000,
                            width: '60px',
                            height: '60px',
                        }}
                    >
                        {/* Top Left Corner */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                width: '15px',
                                height: '15px',
                                borderTop: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderLeft: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderTopLeftRadius: '4px',
                                transition: 'all 0.3s ease',
                            }}
                        />

                        {/* Top Right Corner */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '0',
                                right: '0',
                                width: '15px',
                                height: '15px',
                                borderTop: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderRight: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderTopRightRadius: '4px',
                                transition: 'all 0.3s ease',
                            }}
                        />

                        {/* Bottom Left Corner */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '0',
                                left: '0',
                                width: '15px',
                                height: '15px',
                                borderBottom: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderLeft: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderBottomLeftRadius: '4px',
                                transition: 'all 0.3s ease',
                            }}
                        />

                        {/* Bottom Right Corner */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '0',
                                right: '0',
                                width: '15px',
                                height: '15px',
                                borderBottom: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderRight: `3px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderBottomRightRadius: '4px',
                                transition: 'all 0.3s ease',
                            }}
                        />

                        {/* Center Circle */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: editMode && selectedObservationId ? '16px' : '12px',
                                height: editMode && selectedObservationId ? '16px' : '12px',
                                border: `2px solid ${editMode && selectedObservationId ? theme.palette.primary.main : '#000'}`,
                                borderRadius: '50%',
                                backgroundColor: 'transparent',
                                boxShadow: editMode && selectedObservationId ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    </div>
                )}

                {/* Edit Mode Instruction Tooltip */}
                {/* {editMode && selectedObservationId && showCenterPointer && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(76, 175, 80, 0.95)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            zIndex: 1001,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            pointerEvents: 'none',
                            fontFamily: theme.typography.fontFamily,
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': {
                                    opacity: 0.9,
                                },
                                '50%': {
                                    opacity: 1,
                                },
                            },
                        }}
                    >
                        📍 Pan map, then click center icon to update location
                    </Box>
                )} */}
            </Box>

            {/* Fauna Species Selection Popup */}
            <FaunaSpeciesPopup
                open={showSpeciesPopup}
                onClose={handlePopupClose}
                onSpeciesSelect={handleSpeciesSelect}
                title="Kies de fauna"
            />
        </>
    );
};

export default React.memo(MapComponent);
